import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import withMFD from '@/components/withMFD'
import { withScreenInfo } from '@/components/withScreenInfo'

const Container = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: row;
  color: white;
  overflow: hidden;
  flex-grow: 1;
`

const GridContainer = styled.div<{ rowHeight?: number; maxRows?: number }>`
  width: 100%;
  display: grid;
  grid-gap: 40px;
  grid-template-columns: repeat(auto-fit, 170px);
  grid-template-rows: ${props => props.rowHeight}px;
  justify-content: center;
  align-items: center;
  max-height: 610px;
`

const Collection = styled.div<{ rowHeight?: number }>`
  width: 170px;
  height: ${props => props.rowHeight}px;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  align-self: center;
  justify-self: center;
  position: relative;
  color: white;
`

const CoverArt = styled.div<{ coverUrl?: string }>`
  width: 100%;
  height: 227px;
  ${props => props.coverUrl ? `background-image: url('${props.coverUrl}');` : `background: linear-gradient(180.67deg, rgba(23, 27, 36, 0.8) 0.58%, #171B24 49.49%, rgba(23, 27, 36, 0.8) 99.42%), url('./static/img/gameSwitcher/forge-cover.png'), #181c24;`}
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
`

const Title = styled.div`
  font-size: 16px;
  font-weight: bold;
  line-height: 19px;
  font-family: proxima nova;
  margin-top: 12px;
  text-align: center;
`

const CollectionShardCount = styled.div`
  position: absolute;
  z-index: 4;
  right: 6px;
  top: 5px;
  padding: 3px 5px;
  background: #26393D;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 3px;
  font-family: proxima nova;
  font-weight: 800;
  font-size: 12px;
  line-height: 15px;
  display: flex;
  align-items: center;
  letter-spacing: 0.055em;
  color: #49E69B;

`

interface CollectionItem {
  id: string | number
  shard_count?: number
  cover_url?: string
  name?: string
}

interface CollectionsProps {
  collections?: CollectionItem[]
  redirectToCollection: (id: string | number) => void
  contentRef?: React.Ref<HTMLDivElement>
  maxRows?: number
  rowHeight?: number
  screen?: any
}

const Collections = ({
  collections,
  redirectToCollection,
  contentRef,
  maxRows,
  rowHeight,
  screen
}: CollectionsProps) => {
  const gridRef = useRef<HTMLDivElement>(null)
  const redirectToCollectionMiddleware = useCallback((collectionId: string | number) => {
    redirectToCollection(collectionId)
  }, [redirectToCollection])

  const [scale, setScale] = useState(1)
  useEffect(() => {
    let scaleTimeout: ReturnType<typeof setTimeout>

    const createScaleTimeout = (): ReturnType<typeof setTimeout> => {
      clearTimeout(scaleTimeout)

      return setTimeout(() => {
        let scale:number

        if (gridRef?.current) {
          scale = (gridRef?.current?.parentElement?.clientHeight || rowHeight || 1) / (rowHeight || 1)
        } else {
          return (scaleTimeout = createScaleTimeout())
        }

        if (scale > 1 || scale < 0.25) {
          scale = 1
        }

        setScale(scale)
      }, 0)
    }

    scaleTimeout = createScaleTimeout()

    return () => {
      clearTimeout(scaleTimeout)
    }
  }, [gridRef?.current, collections, screen, rowHeight])

  return (
    <Container
      ref={contentRef as React.Ref<HTMLDivElement>}
    >
      <GridContainer
        ref={gridRef}
        maxRows={maxRows}
        rowHeight={rowHeight}
        style={{
          transform: `scale(${scale})`
        }}
      >
        {collections?.map(collection => {
          const { id, shard_count, cover_url, name } = collection
          return (
            <Collection key={`collection_${id}`} onClick={() => redirectToCollectionMiddleware(id)} rowHeight={rowHeight}>
              <CoverArt coverUrl={cover_url}>{!cover_url && name}</CoverArt>
              <Title>{name}</Title>
              {!!shard_count && (
                <CollectionShardCount>
                  {shard_count}
                </CollectionShardCount>
              )}
            </Collection>
          )
        })}
      </GridContainer>
    </Container>
  )
}

export default withScreenInfo(withMFD(Collections))
